"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { countryLabel } from "@/lib/geo/country-label";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import posthog from "posthog-js";
import { persistSearchTabs, type StoredTab } from "@/app/jobs/tabs-actions";
import { PREMIUM_PRICE_LABEL, UPGRADE_URL } from "@/lib/portfolio/entitlements";
import {
  getSearchDraft,
  subscribeSearchDraft,
} from "@/components/searchDraftStore";
import { setAlertLink } from "@/components/alertLinkStore";
import { orgTypeLabel } from "@/components/format";

/**
 * Multi-tab search for the board. Each tab holds its own search (the board's
 * filter/keyword querystring), so a user can keep several searches going at
 * once — like browser tabs, but for job searches. The active tab always
 * mirrors the current board URL; switching tabs restores that tab's search.
 *
 * This SUBSUMES the old single-slot FilterMemory: the active tab is the
 * remembered search, restored when arriving bare at /jobs.
 *
 * Persistence: signed-out users keep tabs in localStorage (this device only);
 * signed-in users get them saved server-side across devices — the reason to
 * create a free account (which is also the gateway to the portfolio + video
 * recruitment tools).
 */

const LS_KEY = "cm.jobs.tabs.v2";

/** Params that make up a "search" — everything except page/group (transient). */
const REMEMBER = [
  "q",
  "scope",
  "cat",
  "focus",
  "org",
  "type",
  "region",
  "country",
  "city",
  "grade",
  "contract",
  "workplace",
  "closing",
  "posted",
  "remote",
  "sort",
] as const;

interface Tab {
  id: string;
  /** User label; empty string = auto-labelled from the search. */
  label: string;
  /** Canonical board querystring (REMEMBER params only, stable order). */
  qs: string;
  /** Alert-scope querystring this tab's job alert covers, if one exists. */
  alertQs?: string;
}

interface TabsState {
  tabs: Tab[];
  activeId: string;
}

/** Extract only the REMEMBER params, in a stable order, so two equal searches compare equal. */
function normalizeQs(params: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const k of REMEMBER) {
    const vals = params.getAll(k);
    for (const v of vals) if (v) out.append(k, v);
  }
  return out.toString();
}

const REGION_LABEL: Record<string, string> = {
  africa: "Africa",
  americas: "Americas",
  asia_pacific: "Asia-Pacific",
  europe: "Europe",
  middle_east: "Middle East",
};

/** Human label auto-derived from a search's params (keyword first, then a filter, then count). */
function deriveLabel(qs: string): string {
  const p = new URLSearchParams(qs);
  const q = p.get("q")?.trim();
  if (q) return q.length > 22 ? `${q.slice(0, 21)}…` : q;
  const pick =
    p.get("cat") ??
    p.get("focus") ??
    (p.get("region") ? REGION_LABEL[p.get("region")!] ?? p.get("region") : null) ??
    (p.get("country") ? countryLabel(p.get("country")!) : null) ??
    (p.get("city") ? p.get("city")!.slice(3) : null) ??
    (p.get("type") ? orgTypeLabel(p.get("type")!) : null) ??
    (p.get("grade") ? `Grade ${p.get("grade")}` : null) ??
    (p.get("org") ? p.get("org")!.toUpperCase() : null);
  const total = REMEMBER.reduce((n, k) => n + p.getAll(k).length, 0);
  if (pick) {
    const extra = total - 1;
    const base = pick.length > 20 ? `${pick.slice(0, 19)}…` : pick;
    return extra > 0 ? `${base} +${extra}` : base;
  }
  return "All jobs";
}

function tabLabel(t: Tab): string {
  return t.label.trim() || deriveLabel(t.qs);
}

/** A one-line, human summary of a search — keyword + every active filter. */
function summarize(qs: string): string {
  const p = new URLSearchParams(qs);
  const bits: string[] = [];
  const q = p.get("q")?.trim();
  if (q) bits.push(`“${q}”`);
  if (p.get("scope") === "title") bits.push("titles only");
  for (const v of p.getAll("cat")) bits.push(v);
  for (const v of p.getAll("focus")) bits.push(v);
  for (const v of p.getAll("org")) bits.push(v.toUpperCase());
  for (const v of p.getAll("type")) bits.push(orgTypeLabel(v));
  for (const v of p.getAll("region")) bits.push(REGION_LABEL[v] ?? v);
  for (const v of p.getAll("country")) bits.push(v);
  for (const v of p.getAll("grade")) bits.push(`Grade ${v}`);
  for (const v of p.getAll("contract")) bits.push(v);
  for (const v of p.getAll("workplace")) bits.push(v);
  if (p.get("remote")) bits.push("Remote");
  if (p.get("closing")) bits.push("Closing soon");
  if (p.get("posted")) bits.push("Recently posted");
  return bits.join(" · ") || "All jobs";
}

// ---- Recent searches (last N, device-local) ----
const RECENT_KEY = "cm.jobs.recent.v1"; // gitleaks:allow — localStorage key name, not a credential
const RECENT_MAX = 10;
/** A search counts as "recent-worthy" only if it has a keyword or a real filter. */
const MEANINGFUL_KEYS = REMEMBER.filter((k) => k !== "sort" && k !== "scope");
function isMeaningful(p: URLSearchParams): boolean {
  return MEANINGFUL_KEYS.some((k) => !!p.get(k));
}
/** Recent key = the search intent (filters + keyword), independent of ordering. */
function recentKeyOf(p: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const k of REMEMBER) {
    if (k === "sort") continue;
    for (const v of p.getAll(k)) if (v) out.append(k, v);
  }
  return out.toString();
}
function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const a = JSON.parse(raw);
    return Array.isArray(a)
      ? a.filter((x): x is string => typeof x === "string").slice(0, RECENT_MAX)
      : [];
  } catch {
    return [];
  }
}
function saveRecent(list: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* private mode */
  }
}

function ClockIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`h-3.5 w-3.5 shrink-0 ${className}`}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function BellIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`h-3.5 w-3.5 shrink-0 ${className}`}
      aria-hidden
    >
      <path d="M6 9a6 6 0 0112 0c0 5 2 6 2 6H4s2-1 2-6z" />
      <path d="M10 20a2 2 0 004 0" />
    </svg>
  );
}

/** Tab-row popover: a small trigger + a card menu that closes on outside-click/Escape. */
function RowMenu({
  label,
  icon,
  count,
  children,
}: {
  label: string;
  icon: ReactNode;
  count?: number;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12px] font-medium transition-colors ${
          open
            ? "bg-neutral-100 text-ink"
            : "text-neutral-500 hover:bg-neutral-100 hover:text-ink"
        }`}
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
        {count != null && count > 0 ? (
          <span className="rounded-full bg-neutral-200 px-1 text-[9px] font-semibold leading-4 text-neutral-600">
            {count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-[19rem] max-w-[80vw] overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

function newId(): string {
  // MUST be globally unique + stable: a per-session counter resets on reload and
  // collides with tab ids restored from storage (→ URL-sync overwrites the wrong
  // tab). crypto.randomUUID is available in the browser (secure contexts).
  try {
    return crypto.randomUUID();
  } catch {
    return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
  }
}

function loadLocal(): TabsState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TabsState;
    if (!parsed?.tabs?.length) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveLocal(state: TabsState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* private mode — memoryless */
  }
}

export function SearchTabs({
  isAuthed,
  serverTabs,
  currentAlertQs,
  alerts = [],
  maxTabs = 24,
  isPaid = false,
}: {
  isAuthed: boolean;
  /** Saved searches from the DB for signed-in users (source of truth when present). */
  serverTabs?: StoredTab[] | null;
  /** The alert-scope querystring for the URL right now (server-computed). */
  currentAlertQs?: string;
  /** The user's email alerts (each a search querystring) — for the "My alerts" menu. */
  alerts?: { qs: string }[];
  /** Plan cap on the number of tabs (free is limited). */
  maxTabs?: number;
  /** Whether the account is on the paid plan (unlocks the full tab lane). */
  isPaid?: boolean;
}) {
  const sp = useSearchParams();
  const router = useRouter();
  const mountedRef = useRef(false);
  const editingRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<TabsState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tabLimitOpen, setTabLimitOpen] = useState(false);
  const tabLimitDialogRef = useRef<HTMLDialogElement>(null);

  // Keep a ref of the latest state for the URL-sync effect (avoids stale closures
  // and needless effect re-subscription).
  const stateRef = useRef<TabsState | null>(null);
  stateRef.current = state;

  // Debounce the server write so a burst of filter toggles coalesces into one.
  const serverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Persist to localStorage instantly; to the server (debounced) when signed in. */
  const persist = useCallback(
    (next: TabsState) => {
      saveLocal(next);
      if (!isAuthed) return;
      if (serverTimer.current) clearTimeout(serverTimer.current);
      serverTimer.current = setTimeout(() => {
        void persistSearchTabs(
          next.tabs.map((t, i) => ({ label: t.label, qs: t.qs, position: i })),
        ).catch(() => {});
      }, 1200);
    },
    [isAuthed],
  );

  const update = useCallback(
    (fn: (prev: TabsState) => TabsState) => {
      setState((prev) => {
        if (!prev) return prev;
        const next = fn(prev);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  // ---- Initial load + mount reconciliation (restore vs adopt) ----
  useEffect(() => {
    const currentQs = normalizeQs(new URLSearchParams(sp.toString()));

    // Seed from server (authed) or localStorage, else a single default tab.
    let seed: TabsState | null = null;
    if (isAuthed && serverTabs && serverTabs.length > 0) {
      seed = {
        tabs: serverTabs.map((t) => ({ id: newId(), label: t.label, qs: t.qs })),
        activeId: "",
      };
      seed.activeId = seed.tabs[0]!.id;
    } else {
      seed = loadLocal();
    }
    if (!seed || seed.tabs.length === 0) {
      const id = newId();
      seed = { tabs: [{ id, label: "", qs: currentQs }], activeId: id };
    }

    // Reconcile with the URL we actually loaded on.
    const activeIdx = seed.tabs.findIndex((t) => t.id === seed!.activeId);
    const active = seed.tabs[activeIdx >= 0 ? activeIdx : 0]!;

    if (currentQs === "") {
      // Arrived bare (homepage nav, bookmark, new browser tab): restore the
      // active tab's search if it has one.
      if (active.qs !== "") {
        router.replace(`/jobs?${active.qs}`, { scroll: false });
      }
    } else {
      // Arrived with a search (shared link, in-board action before hydration):
      // if a tab already matches it, activate that; otherwise fold it into the
      // active tab so we don't silently spawn duplicates.
      const match = seed.tabs.find((t) => t.qs === currentQs);
      if (match) {
        seed.activeId = match.id;
      } else {
        active.qs = currentQs;
        seed.activeId = active.id;
      }
    }

    mountedRef.current = true;
    setState(seed);
    saveLocal(seed);
    // First sign-in carrying local tabs but nothing saved yet → migrate them
    // up, so "sign in to save" actually keeps the searches you already had.
    if (
      isAuthed &&
      (!serverTabs || serverTabs.length === 0) &&
      (seed.tabs.length > 1 ||
        seed.tabs[0]!.qs !== "" ||
        seed.tabs[0]!.label !== "")
    ) {
      void persistSearchTabs(
        seed.tabs.map((t, i) => ({ label: t.label, qs: t.qs, position: i })),
      ).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Keep the active tab in sync with the URL after mount ----
  useEffect(() => {
    if (!mountedRef.current) return;
    const cur = stateRef.current;
    if (!cur) return;
    const currentQs = normalizeQs(new URLSearchParams(sp.toString()));
    const active = cur.tabs.find((t) => t.id === cur.activeId);
    if (!active || active.qs === currentQs) return;
    update((prev) => ({
      ...prev,
      tabs: prev.tabs.map((t) =>
        t.id === prev.activeId ? { ...t, qs: currentQs } : t,
      ),
    }));
  }, [sp, update]);

  useEffect(() => {
    if (editingId) editingRef.current?.select();
  }, [editingId]);

  // Live "typewriter": while the user types in the search box, the active tab
  // mirrors it letter-by-letter (draft = null when not editing).
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    setDraft(getSearchDraft());
    return subscribeSearchDraft(setDraft);
  }, []);

  // ---- Recent searches (device-local, last 10) ----
  const [recent, setRecent] = useState<string[]>([]);
  useEffect(() => {
    setRecent(loadRecent());
  }, []);
  // Record every committed, meaningful search — fires on URL change (a real
  // search/filter action), never per keystroke (the draft is separate).
  useEffect(() => {
    if (!mountedRef.current) return;
    const params = new URLSearchParams(sp.toString());
    if (!isMeaningful(params)) return;
    const key = recentKeyOf(params);
    if (!key) return;
    setRecent((prev) => {
      const next = [key, ...prev.filter((x) => x !== key)].slice(0, RECENT_MAX);
      if (next.length === prev.length && next.every((x, i) => x === prev[i]))
        return prev;
      saveRecent(next);
      return next;
    });
  }, [sp]);

  // Tell the alert button which search the active tab's alert covers.
  useEffect(() => {
    const active = state?.tabs.find((t) => t.id === state.activeId);
    setAlertLink(active?.alertQs ?? null);
  }, [state]);

  // Native <dialog> gives us focus-trap + Escape-to-close for free; just keep
  // the element's open state in sync with our own (showModal is the only way
  // to open it — the `open` attribute alone skips the backdrop + focus trap).
  useEffect(() => {
    const el = tabLimitDialogRef.current;
    if (!el) return;
    if (tabLimitOpen && !el.open) el.showModal();
    else if (!tabLimitOpen && el.open) el.close();
  }, [tabLimitOpen]);

  // When an alert was just created/updated for this search, link the active tab
  // to it (so a later filter tweak surfaces "Update alert").
  useEffect(() => {
    if (!mountedRef.current || currentAlertQs == null) return;
    const flag = new URLSearchParams(sp.toString()).get("alert");
    if (flag !== "created" && flag !== "updated") return;
    const cur = stateRef.current;
    const active = cur?.tabs.find((t) => t.id === cur.activeId);
    if (active && active.alertQs !== currentAlertQs) {
      update((prev) => ({
        ...prev,
        tabs: prev.tabs.map((t) =>
          t.id === prev.activeId ? { ...t, alertQs: currentAlertQs } : t,
        ),
      }));
    }
  }, [sp, currentAlertQs, update]);

  if (!state) {
    // Reserve height to avoid layout shift before hydration.
    return <div className="h-9" aria-hidden />;
  }

  // stateRef is refreshed on every render (component body) and effects run
  // after render, so handlers just setState + navigate — no manual ref juggling.
  const activate = (id: string) => {
    const t = state.tabs.find((x) => x.id === id);
    if (!t || id === state.activeId) return;
    const next = { ...state, activeId: id };
    setState(next);
    saveLocal(next); // activeId is local-only (server stores the tab list, not focus)
    router.push(t.qs ? `/jobs?${t.qs}` : "/jobs", { scroll: false });
    if (posthog.__loaded) posthog.capture("search_tab_switched");
  };

  /** True once the plan's tab cap is reached (free users). */
  const atTabLimit = !isPaid && state !== null && state.tabs.length >= maxTabs;

  const addTab = () => {
    if (atTabLimit) {
      setTabLimitOpen(true);
      if (posthog.__loaded) posthog.capture("search_tab_limit_hit");
      return;
    }
    const id = newId();
    update((prev) => ({
      tabs: [...prev.tabs, { id, label: "", qs: "" }],
      activeId: id,
    }));
    router.push("/jobs", { scroll: false });
    if (posthog.__loaded) posthog.capture("search_tab_added");
  };

  // Open a recent search or an alert's search as a tab: reuse a tab that already
  // holds the same search, otherwise spawn a new one.
  const openAsTab = (rawQs: string) => {
    const qs = normalizeQs(new URLSearchParams(rawQs));
    const existing = state.tabs.find((t) => t.qs === qs);
    if (existing) {
      if (existing.id !== state.activeId) activate(existing.id);
      else router.push(qs ? `/jobs?${qs}` : "/jobs", { scroll: false });
      return;
    }
    if (atTabLimit) {
      setTabLimitOpen(true);
      return;
    }
    const id = newId();
    update((prev) => ({
      tabs: [...prev.tabs, { id, label: "", qs }],
      activeId: id,
    }));
    router.push(qs ? `/jobs?${qs}` : "/jobs", { scroll: false });
    if (posthog.__loaded) posthog.capture("search_opened_as_tab");
  };

  const closeTab = (id: string) => {
    if (state.tabs.length <= 1) return; // never zero tabs
    const idx = state.tabs.findIndex((t) => t.id === id);
    const remaining = state.tabs.filter((t) => t.id !== id);
    const wasActive = id === state.activeId;
    const nextActive = wasActive
      ? remaining[Math.max(0, idx - 1)]!
      : remaining.find((t) => t.id === state.activeId)!;
    update(() => ({ tabs: remaining, activeId: nextActive.id }));
    if (wasActive) {
      router.push(nextActive.qs ? `/jobs?${nextActive.qs}` : "/jobs", {
        scroll: false,
      });
    }
  };

  const commitRename = (id: string, raw: string) => {
    const label = raw.trim().slice(0, 40);
    update((prev) => ({
      ...prev,
      tabs: prev.tabs.map((t) => (t.id === id ? { ...t, label } : t)),
    }));
    setEditingId(null);
  };

  return (
    <div data-testid="search-tabs" className="relative flex items-center gap-2">
      <dialog
        ref={tabLimitDialogRef}
        onClose={() => setTabLimitOpen(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget) setTabLimitOpen(false);
        }}
        className="w-[20rem] max-w-[85vw] rounded-lg border border-brand-200 bg-white p-3 shadow-lg backdrop:bg-black/40"
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold text-ink">
            {maxTabs} search tabs without membership
          </p>
          <button
            type="button"
            onClick={() => setTabLimitOpen(false)}
            aria-label="Dismiss"
            className="-mr-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-ink"
          >
            ×
          </button>
        </div>
        <p className="mt-1 text-[12px] leading-relaxed text-neutral-600">
          You&rsquo;re running the maximum number of saved search tabs. Upgrade
          to Premium to keep more searches going at once.
        </p>
        <a
          href={UPGRADE_URL}
          onClick={() => {
            if (posthog.__loaded)
              posthog.capture("premium_upsell_clicked", { source: "search_tab_limit" });
          }}
          className="mt-2.5 inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-brand-700"
        >
          ✦ Upgrade to Premium · {PREMIUM_PRICE_LABEL}
        </a>
      </dialog>
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <ul className="flex items-center gap-1">
        {state.tabs.map((t) => {
          const active = t.id === state.activeId;
          const editing = t.id === editingId;
          const typing = active && draft !== null;
          const shownLabel = typing ? draft!.trim() || "All jobs" : tabLabel(t);
          return (
            <li key={t.id} className="shrink-0">
              <div
                className={`group flex h-9 items-center gap-1.5 rounded-t-md border-b-2 px-3 text-[13px] transition-colors ${
                  active
                    ? "border-brand-600 bg-white font-semibold text-ink"
                    : "border-transparent text-neutral-500 hover:bg-neutral-100 hover:text-ink"
                }`}
              >
                {editing ? (
                  <input
                    ref={editingRef}
                    defaultValue={tabLabel(t)}
                    onBlur={(e) => commitRename(t.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(t.id, e.currentTarget.value);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    className="w-28 rounded border border-brand-300 bg-white px-1 py-0.5 text-[13px] text-ink focus:outline-none focus:ring-1 focus:ring-brand-600"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => activate(t.id)}
                    onDoubleClick={() => setEditingId(t.id)}
                    title={active ? "Double-click to rename" : tabLabel(t)}
                    className="flex max-w-[12rem] items-center"
                  >
                    <span className="truncate">{shownLabel}</span>
                    {typing ? (
                      <span
                        aria-hidden
                        className="ml-px inline-block h-[1.05em] w-px shrink-0 animate-pulse bg-current"
                      />
                    ) : null}
                  </button>
                )}
                {state.tabs.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => closeTab(t.id)}
                    aria-label={`Close ${tabLabel(t)}`}
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-200 hover:text-ink ${
                      active ? "" : "opacity-0 group-hover:opacity-100"
                    }`}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

        <button
          type="button"
          onClick={addTab}
          aria-label={atTabLimit ? "Upgrade to add more search tabs" : "New search tab"}
          title={atTabLimit ? "Upgrade to add more search tabs" : "New search tab"}
          className="ml-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-lg leading-none text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
        >
          +
        </button>
      </div>

      {/* Right-side controls: recent searches + email alerts (or a sign-in nudge). */}
      <div className="flex shrink-0 items-center gap-0.5 border-l border-neutral-200 pl-1.5">
        <RowMenu label="Recent" icon={<ClockIcon />} count={recent.length}>
          {(close) =>
            recent.length === 0 ? (
              <p className="px-3 py-4 text-[12px] leading-relaxed text-neutral-500">
                No recent searches yet. Searches you run show up here so you can
                jump straight back to them.
              </p>
            ) : (
              <ul className="max-h-[19rem] overflow-y-auto py-1">
                {recent.map((qs) => (
                  <li key={qs}>
                    <button
                      type="button"
                      onClick={() => {
                        openAsTab(qs);
                        close();
                      }}
                      className="group flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-neutral-50"
                    >
                      <ClockIcon className="text-neutral-300" />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                        {summarize(qs)}
                      </span>
                      <span className="shrink-0 text-[10px] font-medium text-neutral-300 group-hover:text-brand-600">
                        Open ↗
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )
          }
        </RowMenu>

        {isAuthed ? (
          <RowMenu label="My alerts" icon={<BellIcon />} count={alerts.length}>
            {(close) => (
              <div>
                <p className="telex border-b border-neutral-100 px-3 py-2 text-[9px] text-neutral-400">
                  Email alerts · new matches only
                </p>
                {alerts.length === 0 ? (
                  <p className="px-3 py-4 text-[12px] leading-relaxed text-neutral-500">
                    No alerts yet. Open a search and hit{" "}
                    <span className="font-medium text-ink">Create alert</span> to
                    get new matches by email.
                  </p>
                ) : (
                  <ul className="max-h-[19rem] overflow-y-auto py-1">
                    {alerts.map((a) => (
                      <li key={a.qs}>
                        <button
                          type="button"
                          onClick={() => {
                            openAsTab(a.qs);
                            close();
                          }}
                          className="group flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-neutral-50"
                        >
                          <BellIcon className="text-brand-400" />
                          <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                            {summarize(a.qs)}
                          </span>
                          <span className="shrink-0 whitespace-nowrap text-[10px] font-medium text-neutral-300 group-hover:text-brand-600">
                            Open as tab ↗
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <a
                  href="/dashboard"
                  className="block border-t border-neutral-100 px-3 py-2 text-[11px] font-medium text-brand-700 transition-colors hover:bg-neutral-50"
                >
                  Manage alerts →
                </a>
              </div>
            )}
          </RowMenu>
        ) : (
          <a
            href="/auth/login?next=/jobs"
            className="shrink-0 whitespace-nowrap px-2 text-[12px] font-medium text-brand-700 hover:text-brand-800"
            title="Sign in to keep your search tabs on every device"
          >
            Sign in to save →
          </a>
        )}
      </div>
    </div>
  );
}
